import axios from 'axios';

// ---------------------------------------------------------------------------
// Auth token transport strategy (updated 06/07/2026)
// ---------------------------------------------------------------------------
// Backend now sets an httpOnly Secure SameSite=None cookie `session_token`
// on every login AND accepts a fallback `Authorization: Bearer <token>`
// header (kept for cross-origin preview environments).
//
// This module picks the strongest available transport at runtime:
//
//   ┌─────────────────────────────┬──────────────────┬─────────────────────┐
//   │ Environment                 │ withCredentials  │ Bearer fallback     │
//   ├─────────────────────────────┼──────────────────┼─────────────────────┤
//   │ Same-origin (production     │  TRUE            │ still sent          │
//   │   pxgroup.it)               │  (cookie active) │   (defense-in-depth)│
//   ├─────────────────────────────┼──────────────────┼─────────────────────┤
//   │ Cross-origin preview        │  FALSE           │ REQUIRED            │
//   │   (Cloudflare returns       │  (would break    │   (only transport   │
//   │   ACAO:* which is           │   with           │   that works)       │
//   │   incompatible with         │   credentials)   │                     │
//   │   withCredentials)          │                  │                     │
//   └─────────────────────────────┴──────────────────┴─────────────────────┘
//
// In production XSS attackers CANNOT read the httpOnly cookie — the localStorage
// token becomes redundant defense-in-depth. It stays for backward compatibility
// with existing browser sessions.
// ---------------------------------------------------------------------------

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// True when frontend and backend are on the same origin (production case).
// This is the ONLY situation where cookies can flow safely across requests.
function isSameOrigin() {
  try {
    const backendOrigin = new URL(BACKEND_URL).origin;
    return backendOrigin === window.location.origin;
  } catch (e) {
    return false;
  }
}

const SAME_ORIGIN = isSameOrigin();

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // Only send/receive cookies when same-origin (avoids CORS breakage in preview).
  withCredentials: SAME_ORIGIN,
});

// Get token from any available source (Bearer fallback).
function getToken() {
  // Priority: sessionStorage > localStorage > readable cookie
  const ss = sessionStorage.getItem("px_session");
  if (ss) return ss;
  const ls = localStorage.getItem("px_session_token");
  if (ls) return ls;
  const match = document.cookie.match(/(?:^|;\s*)px_token=([^;]*)/);
  if (match) return match[1];
  return null;
}

// Add Bearer header to every request when a token is available.
// In production this is redundant (httpOnly cookie already covers auth) but
// harmless — the backend accepts either transport.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;
